/**
 * TPG Player — Presentation Strategy resolver (Sprint 8 Phase B1).
 *
 * Maps a Media Asset to a presentation strategy. One player shell; many
 * strategies. No separate player implementations per media type.
 */

import {
  MediaPresentationKind,
  MediaSourceType,
  MediaSurfaceKind,
  inferPresentationKind,
  inferSourceType,
  createMediaAsset,
} from "./MediaAsset";

/** Strategy ids used by the shell / lab / future host wiring. */
export var PresentationStrategyId = Object.freeze({
  VIDEO: "video",
  AUDIO: "audio",
  COMPANION: "companion",
  PLAYLIST: "playlist",
  CHANNEL: "channel",
  EMBED: "embed",
  LIVE: "live",
  SCHEDULED_LIVE: "scheduled_live",
  INTERACTIVE: "interactive",
  UNSUPPORTED: "unsupported",
});

/**
 * @typedef {object} PresentationStrategy
 * @property {string} id
 * @property {string} surfaceKind
 * @property {string} presentationKind
 * @property {string} sourceType
 * @property {boolean} usesVideoElement
 * @property {boolean} usesAudioElement
 * @property {boolean} usesCompanionImage
 * @property {boolean} usesEmbed
 * @property {boolean} isCollection
 * @property {boolean} isLive
 * @property {string[]} defaultCapabilities
 * @property {string} [adapterHint]  progressive | hls | youtube-embed | vimeo-embed | companion | …
 */

/**
 * @param {object} asset  MediaAsset or legacy-shaped object
 * @returns {PresentationStrategy}
 */
export function resolvePresentationStrategy(asset) {
  asset = createMediaAsset(
    Object.assign({}, asset || {}, {
      presentationKind:
        (asset && asset.presentationKind) || inferPresentationKind(asset || {}),
      sourceType: (asset && asset.sourceType) || inferSourceType(asset || {}),
    })
  );

  var kind = asset.presentationKind;
  var source = asset.sourceType;

  if (
    source === MediaSourceType.YOUTUBE ||
    source === MediaSourceType.VIMEO ||
    kind === MediaPresentationKind.EMBED
  ) {
    return {
      id: PresentationStrategyId.EMBED,
      surfaceKind: MediaSurfaceKind.EMBED,
      presentationKind: kind,
      sourceType: source,
      usesVideoElement: false,
      usesAudioElement: false,
      usesCompanionImage: false,
      usesEmbed: true,
      isCollection: false,
      isLive: false,
      // Include subtitles so the control stays visible; embeds have no
      // TPG caption API — selecting On shows the unavailable toast.
      defaultCapabilities: ["analytics", "fullscreen", "subtitles"],
      adapterHint:
        source === MediaSourceType.VIMEO ? "vimeo-embed" : "youtube-embed",
    };
  }

  if (kind === MediaPresentationKind.CHANNEL) {
    return {
      id: PresentationStrategyId.CHANNEL,
      surfaceKind: MediaSurfaceKind.CHANNEL,
      presentationKind: kind,
      sourceType: source,
      usesVideoElement: true,
      usesAudioElement: false,
      usesCompanionImage: false,
      usesEmbed: false,
      isCollection: true,
      isLive: true,
      defaultCapabilities: ["live", "dvr", "analytics", "subtitles"],
      adapterHint: "hls",
    };
  }

  if (
    kind === MediaPresentationKind.PLAYLIST ||
    kind === MediaPresentationKind.ALBUM ||
    kind === MediaPresentationKind.PODCAST ||
    kind === MediaPresentationKind.AUDIOBOOK
  ) {
    var audioCollection =
      kind === MediaPresentationKind.ALBUM ||
      kind === MediaPresentationKind.PODCAST ||
      kind === MediaPresentationKind.AUDIOBOOK;
    return {
      id: PresentationStrategyId.PLAYLIST,
      surfaceKind: MediaSurfaceKind.PLAYLIST,
      presentationKind: kind,
      sourceType: source,
      usesVideoElement: !audioCollection,
      usesAudioElement: audioCollection,
      usesCompanionImage: audioCollection,
      usesEmbed: false,
      isCollection: true,
      isLive: false,
      defaultCapabilities: ["analytics", "resume", "chapters"],
      adapterHint: audioCollection ? "companion" : "progressive",
    };
  }

  if (
    kind === MediaPresentationKind.IMAGE_AUDIO ||
    (asset.companion && (asset.companion.image || asset.companion.audio))
  ) {
    return {
      id: PresentationStrategyId.COMPANION,
      surfaceKind: MediaSurfaceKind.COMPOSITE,
      presentationKind: MediaPresentationKind.IMAGE_AUDIO,
      sourceType: MediaSourceType.COMPANION,
      usesVideoElement: false,
      usesAudioElement: true,
      usesCompanionImage: true,
      usesEmbed: false,
      isCollection: false,
      isLive: false,
      defaultCapabilities: [
        "companion",
        "analytics",
        "playbackSpeed",
        "resume",
      ],
      adapterHint: "companion",
    };
  }

  if (
    kind === MediaPresentationKind.AUDIO ||
    kind === MediaPresentationKind.PODCAST
  ) {
    return {
      id: PresentationStrategyId.AUDIO,
      surfaceKind: MediaSurfaceKind.AUDIO,
      presentationKind: kind,
      sourceType: source,
      usesVideoElement: false,
      usesAudioElement: true,
      usesCompanionImage: false,
      usesEmbed: false,
      isCollection: false,
      isLive: false,
      defaultCapabilities: ["analytics", "playbackSpeed", "resume"],
      adapterHint: "companion",
    };
  }

  if (kind === MediaPresentationKind.SCHEDULED_LIVE) {
    return {
      id: PresentationStrategyId.SCHEDULED_LIVE,
      surfaceKind: MediaSurfaceKind.VIDEO,
      presentationKind: kind,
      sourceType: source,
      usesVideoElement: true,
      usesAudioElement: false,
      usesCompanionImage: false,
      usesEmbed: false,
      isCollection: false,
      isLive: true,
      defaultCapabilities: ["live", "analytics", "eventCountdown"],
      adapterHint: "hls",
    };
  }

  if (kind === MediaPresentationKind.LIVE) {
    return {
      id: PresentationStrategyId.LIVE,
      surfaceKind: MediaSurfaceKind.VIDEO,
      presentationKind: kind,
      sourceType: source,
      usesVideoElement: true,
      usesAudioElement: false,
      usesCompanionImage: false,
      usesEmbed: false,
      isCollection: false,
      isLive: true,
      defaultCapabilities: ["live", "dvr", "analytics"],
      adapterHint: "hls",
    };
  }

  if (
    kind === MediaPresentationKind.INTERACTIVE ||
    kind === MediaPresentationKind.SPATIAL
  ) {
    return {
      id: PresentationStrategyId.INTERACTIVE,
      surfaceKind: MediaSurfaceKind.COMPOSITE,
      presentationKind: kind,
      sourceType: source,
      usesVideoElement: true,
      usesAudioElement: false,
      usesCompanionImage: false,
      usesEmbed: false,
      isCollection: false,
      isLive: false,
      defaultCapabilities: ["analytics", "interactive"],
      adapterHint: "progressive",
    };
  }

  if (
    kind === MediaPresentationKind.VIDEO ||
    kind === MediaPresentationKind.EXTERNAL ||
    asset.primary
  ) {
    return {
      id: PresentationStrategyId.VIDEO,
      surfaceKind: MediaSurfaceKind.VIDEO,
      presentationKind:
        kind === MediaPresentationKind.EXTERNAL
          ? MediaPresentationKind.EXTERNAL
          : MediaPresentationKind.VIDEO,
      sourceType: source || MediaSourceType.HOSTED,
      usesVideoElement: true,
      usesAudioElement: false,
      usesCompanionImage: false,
      usesEmbed: false,
      isCollection: false,
      isLive: false,
      defaultCapabilities: [
        "subtitles",
        "audioTracks",
        "playbackSpeed",
        "analytics",
        "resume",
        "theater",
        "watermark",
      ],
      adapterHint: "progressive",
    };
  }

  return {
    id: PresentationStrategyId.UNSUPPORTED,
    surfaceKind: MediaSurfaceKind.VIDEO,
    presentationKind: kind,
    sourceType: source,
    usesVideoElement: false,
    usesAudioElement: false,
    usesCompanionImage: false,
    usesEmbed: false,
    isCollection: false,
    isLive: false,
    defaultCapabilities: [],
    adapterHint: null,
  };
}

export default {
  PresentationStrategyId,
  resolvePresentationStrategy,
};
